/**
 * @module Flash
 */

import {ClientApplication} from '../../client';
import {actionWrapper, getMergedProps, ActionSet} from '../helper';
import {ActionSetProps, Group, MetaAction} from '../types';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
} from '../Error';
import {Action, ActionType, ClearPayload, Options, Payload} from './types';

export interface ActionBase extends MetaAction {
  readonly group: typeof Group.Flash;
}
export interface ShowAction extends ActionBase {
  readonly type: typeof ActionType.SHOW;
  readonly payload: Payload;
}
export interface ClearAction extends ActionBase {
  readonly type: typeof ActionType.CLEAR;
}

export type FlashAction = ShowAction | ClearAction | MetaAction;

export function show(flashMessage: Payload): ShowAction {
  return actionWrapper({
    group: Group.Flash,
    payload: flashMessage,
    type: ActionType.SHOW,
  });
}

export function clear(payload: ClearPayload): ClearAction {
  return actionWrapper({
    payload,
    group: Group.Flash,
    type: ActionType.CLEAR,
  });
}

export function validationError(
  action: ShowAction | ClearAction | MetaAction,
): undefined | ErrorAction {
  let payload;
  switch (action.type) {
    case ActionType.CLEAR:
      payload = (action as ClearAction).payload;

      return payload === undefined || (payload.id === undefined || typeof payload.id === 'string')
        ? undefined
        : invalidPayload(action, 'Id in payload is invalid');

    case ActionType.SHOW:
      payload = (action as ShowAction).payload;

      return payload !== undefined &&
        (payload.id === undefined || typeof payload.id === 'string') &&
        isValidFlashProps(payload)
        ? undefined
        : invalidPayload(action);
    default:
      return invalidActionType(action);
  }
}

function isValidFlashProps(props: Payload) {
  return typeof props.message === 'string' && typeof props.duration === 'number';
}

export class Flash extends ActionSet implements ActionSetProps<Options, Payload> {
  message = '';
  isDismissible?: boolean;
  duration = 3000;
  isError?: boolean;
  constructor(app: ClientApplication<any>, options: Options) {
    super(app, Group.Flash, Group.Flash);
    this.set(options);
  }

  get options(): Options {
    return {
      duration: this.duration,
      isDismissible: this.isDismissible,
      isError: this.isError,
      message: this.message,
    };
  }

  get payload(): Payload {
    return {
      id: this.id,
      ...this.options,
    };
  }

  set(options: Partial<Options>) {
    const mergedOptions = getMergedProps(this.options, options);
    const {message, isDismissible, duration, isError} = mergedOptions;

    if (!this.validateOptions(mergedOptions)) {
      return this;
    }

    this.message = message;
    this.isDismissible = isDismissible;
    this.duration = duration;
    this.isError = isError;

    return this;
  }

  dispatch(action: Action) {
    switch (action) {
      case Action.SHOW:
        const openAction = show(this.payload);
        this.app.dispatch(openAction);
        break;
      case Action.CLEAR:
        this.app.dispatch(clear({id: this.id}));
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }

  private validateOptions(options: Options) {
    if (!isValidFlashProps(options)) {
      const errorAction = actionWrapper({
        group: Group.Flash,
        payload: {id: this.id, ...options},
      });
      throwError(ErrorActionType.INVALID_OPTIONS, errorAction);

      return false;
    }

    return true;
  }
}

export function create(app: ClientApplication<any>, options: Options) {
  return new Flash(app, options);
}



// WEBPACK FOOTER //
// ./src/actions/Flash/actions.ts